import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

const Container = styled.div`
  &[data-reveal='pending'] {
    opacity: 0;
    transform: translateY(20px);
  }

  &[data-reveal='visible'] {
    transition:
      opacity 500ms cubic-bezier(0.22, 1, 0.36, 1) var(--reveal-delay, 0ms),
      transform 500ms cubic-bezier(0.22, 1, 0.36, 1) var(--reveal-delay, 0ms);
  }

  @media (prefers-reduced-motion: reduce) {
    &[data-reveal] {
      opacity: 1;
      transform: none;
      transition: none;
    }
  }
`;

/**
 * Fades and lifts its children into view the first time they scroll on screen.
 *
 * CSS + IntersectionObserver only. The server (and the first client render)
 * output the content fully visible, so nothing is hidden if JavaScript fails
 * and there is no hydration mismatch. After mount, only content still below
 * the fold is hidden and then revealed on scroll; content already on screen
 * is left alone. `prefers-reduced-motion` turns the effect off.
 *
 *   <Reveal>…</Reveal>
 *   {items.map((item, i) => <Reveal key={item.id} delay={i * 80}>…</Reveal>)}
 */
const Reveal = ({ as, children, className, delay = 0, style, ...props }) => {
  const ref = useRef(null);
  const [state, setState] = useState('static');

  useEffect(() => {
    const node = ref.current;

    if (
      !node ||
      typeof IntersectionObserver === 'undefined' ||
      typeof window.matchMedia !== 'function' ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      return undefined;
    }

    // Already on screen (or above it): never hide what the visitor can see.
    if (node.getBoundingClientRect().top < window.innerHeight) {
      return undefined;
    }

    setState('pending');

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setState('visible');
          observer.disconnect();
        }
      },
      // A small threshold so blocks taller than the viewport still reveal.
      { threshold: 0.05, rootMargin: '0px 0px -80px 0px' }
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  return (
    <Container
      as={as}
      className={className}
      data-reveal={state}
      ref={ref}
      style={delay ? { ...style, '--reveal-delay': `${delay}ms` } : style}
      {...props}
    >
      {children}
    </Container>
  );
};

export default Reveal;
